import React, { useCallback } from "react";
import { LoadingIndicator, ErrorMessage } from "@jsq/ds/core";
import { PaymentMethod } from "../../../../common/enums";
import { useConsolidatedPaymentPrefContext } from "../../Context";
import { useGetDetailedPaymentPref } from "../../hooks/use-get-detailed-payment-pref";
import { useSetBankCountry } from "../../hooks/use-set-bank-country";
import { getIdFromCountry } from "../../mappers";
import { PaymentPrefLayoutForm } from "../PaymentPrefLayoutForm";
import { type ChildComponentProps } from "../types";
import { AccountDetails } from "./AccountDetails";
import { BankDetails } from "./BankDetails";
import { BankDetailsInputFields } from "./BankDetails/constants";
import { SepaPaymentPrefInputSchema, type SepaPaymentPrefInputType } from "./constants";
import { mapContextStateToFormState, mapFormStateToContextState } from "./utils";

export const SepaPaymentPrefInput: React.FC<ChildComponentProps> = ({
  onContinueClick,
  onBackClick,
  onBackButtonName = "Back",
}) => {
  const { setPaymentPrefDetail, workflowMode, selectedBankCountry } =
    useConsolidatedPaymentPrefContext();
  const { data: paymentPrefDetail, loading, error } = useGetDetailedPaymentPref();

  const {
    loading: countryLoading,
    error: countryError,
    resolveAndSet,
  } = useSetBankCountry({ paymentPrefDetail });

  const handleFormSubmission = useCallback(
    (values: SepaPaymentPrefInputType) => {
      // Compute the effective bank country synchronously so mapFormStateToContextState
      // always receives a fully-resolved value regardless of async state updates.
      const effectiveBankCountry =
        selectedBankCountry ??
        resolveAndSet({
          countryId: getIdFromCountry(values[BankDetailsInputFields.BankCountry]),
        });

      setPaymentPrefDetail(mapFormStateToContextState(values, effectiveBankCountry));
      onContinueClick();
    },
    [setPaymentPrefDetail, onContinueClick, selectedBankCountry, resolveAndSet],
  );

  if (loading || countryLoading) {
    return <LoadingIndicator />;
  }

  if (error || countryError) {
    return <ErrorMessage>Failed to load payment preference</ErrorMessage>;
  }

  return (
    <PaymentPrefLayoutForm
      paymentMethod={PaymentMethod.SEPA}
      onSubmit={handleFormSubmission}
      initialValues={mapContextStateToFormState(
        paymentPrefDetail?.__typename === "SepaPaymentPrefObjectType"
          ? paymentPrefDetail
          : undefined,
        selectedBankCountry,
      )}
      validationSchema={SepaPaymentPrefInputSchema}
      onBackClick={onBackClick}
      onBackButtonName={onBackButtonName}
      workflowMode={workflowMode}
    >
      <BankDetails />
      <AccountDetails />
    </PaymentPrefLayoutForm>
  );
};
